// All the words on the site live here. Edit this file to change content.
import wathulanda from './team/wathulanda.png';
import wakishta from './team/wakishta.png';
import pramodhya from './team/pramodhya.png';
import samarasinghe from './team/samarasinghe.png';

export const project = {
  name: 'SkillBridge',
  title: 'Adaptive Programming Learning Pathways Using Schema and Emotion Analysis',
  summary:
    'A final-year research project that personalises introductory Java practice by reading two things at once: what a learner knows and how they feel while learning it.',
  liveUrl: 'https://slskillbridgex.com/',
  documentsUrl: 'https://mysliit-my.sharepoint.com/:f:/g/personal/it22253026_my_sliit_lk/IgBUX9tKR01VTqZKP3t3_hrNATGy1YP3nEkPY8r0tuMsfr8?e=dlLSbT',
  facts: [
    ['Project ID', 'R26-IT-033'],
    ['Module', 'IT4010 Research Project, 2026 January'],
    ['Research group', 'CoEAI, Centre of Excellence for AI'],
    ['Specialization', 'Information Technology'],
    ['Institute', 'Sri Lanka Institute of Information Technology'],
  ],
};

export const introduction = [
  'Learning to program is hard for beginners. Confusion, stress and boredom show up early, and in self-paced digital environments there is rarely anyone watching closely enough to notice before the learner gives up.',
  'SkillBridge is an adaptive learning platform for introductory Java. It combines a learner’s knowledge schema with their emotional state and uses both to decide what happens next: a motivational message, a study tip, an easier or harder question, a set of targeted revision questions, or a recommendation to revisit prerequisites.',
  'It is built for first-year IT and computer science students. They work through an adaptive Java multiple-choice course at Basic and Pro levels, covering variables, operators, selections, iterations, arrays, methods and language fundamentals.',
  'The platform is built from five research components that run as containerized microservices behind a single API gateway. Every intermediate model output is visible on an administrator dashboard, so each adaptive decision can be traced and its thresholds tuned without redeploying.',
];

export const researchGap = {
  lead: 'Emotion recognition, difficulty prediction, mistake diagnosis and exercise generation all exist in the literature. They are almost always built separately, so the signal one of them produces never reaches the others.',
  points: [
    ['Emotion systems', 'detect frustration but do not use it to diagnose mistakes or adjust question difficulty.'],
    ['Knowledge-gap systems', 'identify weak concepts without considering how the learner felt while answering.'],
    ['Difficulty models', 'rate how hard a question is in general, not how hard it will be for this learner given their effort and readiness.'],
    ['Question generators', 'produce exercises without recent learner performance and never recalibrate difficulty from real use.'],
    ['Learning analytics', 'report on what already happened instead of turning signals into an immediate next action.'],
  ],
  close:
    'Limited research combines learner readiness, cognitive effort, question characteristics, emotion, mistake diagnosis, exercise generation and unified analytics in one closed-loop pathway for programming education.',
};

export const problem =
  'Platforms such as Codecademy, HackerRank and Moodle give every learner the same fixed exercises. Where adaptation exists, it reacts only after the student has already performed poorly, and it ignores the emotional signals that come before a learner withdraws. Students keep struggling without timely support.';

export const solution =
  'SkillBridge closes the loop. While a learner answers Java questions, the platform watches for persistent negative emotion, works out why a wrong answer was wrong, predicts how hard the next question will be for that specific learner, generates and recalibrates the questions themselves, and fuses every signal into one pathway stage with one recommended next action.';

export const mainObjective =
  'Design and develop a platform that personalises programming-education pathways by jointly analysing a learner’s knowledge schema and emotional state, so support arrives while the learner is struggling rather than after they have failed.';

export const components = [
  {
    id: 'emotion',
    short: 'Emotion',
    name: 'Emotion-Triggered Interventions',
    owner: 'Samarasinghe B.M.S.V',
    subObjective:
      'Detect the learner’s emotional state during a quiz session and respond with hints, study tips and motivational prompts that reduce frustration and keep them engaged.',
    how: 'The browser captures a webcam frame every 250 ms, resizes it to 320 × 240 and sends it to the emotion service. A calibration file built from about 24,000 labelled images corrects the raw scores, and a five-sample rolling buffer with majority voting and a 55% confidence threshold filters out one-off predictions. When fear, disgust, sadness or anger persists for five seconds, the learner sees an LLM-written motivational message or a tip for the question in front of them. Looking away for eight seconds produces an attention nudge.',
    novelty:
      'The emotion does not stop at detection. It triggers support during the question, feeds the behavioural diagnosis of mistakes, and tip engagement flows back into question-bank calibration.',
  },
  {
    id: 'gap',
    short: 'Gaps',
    name: 'Knowledge Gap Localization',
    owner: 'Pramodhya M.V.P.',
    subObjective:
      'Identify the missing knowledge behind a learner’s repeated mistakes and guide them back to the right concept with targeted revision instead of a full lesson.',
    how: 'For each incorrect answer the system combines the concept, question, code, selected answer and correct answer. A TF-IDF vectorizer over unigrams and bigrams feeds a balanced Random Forest that predicts one of six error types, from a minor calculation error to a complete misunderstanding. A keyword-weighted detector maps the response to one of fourteen Java concept categories, and a rule-based classifier uses attempts, response time, live emotion and recent accuracy to name the probable cause: careless mistake, rushing, frustration, low confidence, conceptual misunderstanding or knowledge gap.',
    novelty:
      'It explains why an answer was wrong, not only that it was wrong, and it uses the learner’s live emotion as evidence. Weak topics are ranked at the end of a session and sent to the exercise generator.',
  },
  {
    id: 'difficulty',
    short: 'Difficulty',
    name: 'Difficulty Prediction Model',
    owner: 'Wakishta C.S',
    subObjective:
      'Analyse how a learner interacts with questions and predict how difficult the next candidate question will be for that learner.',
    how: 'A Random Forest classifier predicts whether a learner will experience a candidate next question as Easy, Medium or Hard. It uses seventeen features covering correctness, response time, attempts, cognitive effort, learner readiness, recent accuracy and effort, learning trend, Question Difficulty Index, expected solving time, concept count, Bloom’s level and historical success rate. A score-band rule is used only as a clearly tagged fallback when the trained model is unavailable.',
    novelty:
      'The prediction is specific to the learner. It combines their current state with the question’s characteristics instead of classifying question difficulty in general.',
  },
  {
    id: 'generator',
    short: 'Exercises',
    name: 'Adaptive Coding Exercise Generator',
    owner: 'Wathulanda S.R.W.H.N.B',
    subObjective:
      'Generate syllabus-aligned Java exercises that match the learner’s current level, and keep their difficulty calibrated from how learners actually answer them.',
    how: 'A large language model (gpt-4o-mini by default) writes Java multiple-choice questions with a code snippet, four options and a Bloom level of 1 or 2. Each question has one most-correct answer, one valid but less complete answer and two distractors, so the choice itself is an answer-quality signal. Questions are served through a diagnostic phase and then an adaptive course phase. A nightly job recalculates difficulty from incorrect-answer rates, retires questions above a threshold and refills each difficulty bucket.',
    novelty:
      'The question bank calibrates itself from real use, and tip views from the emotion component nudge a question’s difficulty, linking affective data to content calibration.',
  },
  {
    id: 'analytics',
    short: 'Analytics',
    name: 'Unified Learning Analytics',
    owner: 'Whole team',
    subObjective:
      'Fuse the cognitive, behavioural and emotional signals from every component into one learner profile and one recommended next action.',
    how: 'Every five minutes an aggregation job reads learner records from all component databases and builds a student profile: overall learning score, programming competency, schema-development level, knowledge-gap and misconception scores, emotional stability, per-topic mastery and a breakdown of mistake reasons. The overall score places the learner in one of four pathway stages (foundation, building, consolidating, advanced), and a priority-ordered rule engine picks one of eight next actions.',
    novelty:
        'Analytics acts instead of only reporting. The same profile is shown to administrators and to the learner.',
  },
];

export const sessionFlow = [
  ['The learner answers a question', 'The platform records the choice, response time and number of attempts while the webcam pipeline tracks emotion and gaze.'],
  ['Emotion is checked', 'Persistent negative emotion triggers a motivational message or a tip for that question. Neutral and happy states trigger nothing.'],
  ['A wrong answer is diagnosed', 'Knowledge Gap Localization names the error type, the Java concept involved and the probable behavioural cause.'],
  ['The next difficulty is predicted', 'The Difficulty Prediction Model classifies the next candidate question as Easy, Medium or Hard for this learner.'],
  ['The next question is served', 'The generator supplies a question at that difficulty. The topic comes from syllabus rotation or from the learner’s weak topics.'],
  ['The pathway is updated', 'Unified Learning Analytics refreshes the learner profile, pathway stage and recommended next action, and the loop repeats.'],
];

// Shared by the methodology cards and the diagrams, so each fact is written once.
export const readinessFormula = 'LR = 0.35 NS + 0.35 (1 − CE) + 0.20 RA + 0.10 × (LT + 1) ÷ 2';

// Priority, condition, action. The first matching row wins.
export const nextActions = [
  ['1', 'Dominant mistake is knowledge gap and KnowledgeGap > 60', 'Review prerequisites'],
  ['2', 'Dominant mistake is conceptual misunderstanding and Misconception > 50', 'Watch materials'],
  ['3', 'Dominant mistake is frustrated/stuck, or EmotionalStability < 40', 'Instructor support'],
  ['4', 'Dominant mistake is careless and Score > 65', 'Increase difficulty'],
  ['5', 'Dominant mistake is low confidence and Confidence < 50', 'Decrease difficulty'],
  ['6', 'Revision effectiveness < 0 and weak topics exist', 'Start revision'],
  ['7', 'Weak topics exist', 'Practice similar'],
  ['8', 'None of the above', 'Continue pathway'],
];

// Every method the system uses, grouped by component. Each item is one card and can carry:
// what (plain-language purpose), strip (numbered steps), expr (formula lines), terms ([name, meaning]),
// table ({ head, rows }), steps (ordered rules), points (bullets), example and note.
export const methodology = {
  groups: [
    {
      id: 'm-overview',
      nav: 'Overview',
      title: 'Overview',
      intro: 'The platform runs a closed loop. Each answer produces signals, and each signal feeds the next decision.',
      items: [
        {
          title: 'How a decision flows',
          what: 'One answer moves through six steps before the next question appears.',
          strip: sessionFlow,
        },
        {
          title: 'How a decision is made',
          what: 'Each of the five components makes one part of the decision.',
          steps: [
            'Emotion service reads webcam frames and decides whether to show a tip or a motivational message.',
            'For a wrong answer, Knowledge Gap Localization names the error type, the concept and the likely behavioural cause.',
            'The Difficulty Prediction Model turns the answer into a score, a cognitive-effort value and a readiness value. A Random Forest then predicts Easy, Medium or Hard for the next question.',
            'The Exercise Generator serves a question from that difficulty bucket. Its difficulty value (DV) is recalculated nightly from real use.',
            'Unified Learning Analytics merges everything into one profile, one pathway stage and one next action.',
          ],
        },
        {
          title: 'Scales shared across the system',
          what: 'Every component uses the same values and cut-offs, so “high” means the same thing wherever it appears.',
          terms: [
            ['Answer value', 'most_correct = 1, suitable = 0.5, incorrect = 0.'],
            ['Band cut-offs', '0.7 = high / hard / strong and 0.4 = low / medium / developing. This applies to QDI bands, readiness bands, effort bands, topic status and weakness bands.'],
            ['Negative emotion', 'the same set everywhere: fearful, disgusted, sad, angry, surprised.'],
            ['Fallbacks', 'every ML decision has a clearly tagged rule-based fallback, so one failing service never breaks the learner’s session. The prediction source is stored with each decision.'],
          ],
        },
      ],
    },
    {
      id: 'm-emotion',
      nav: 'Emotion',
      title: 'Emotion-Triggered Interventions',
      kicker: 'Novelty 1',
      intro: 'Decides whether the learner needs a tip or a motivational message while they are still on the question.',
      items: [
        {
          title: 'Face detection',
          what: 'Finds the learner’s face in each webcam frame and keeps the most reliable detection. The service uses face-api.js with the TinyFaceDetector.',
          expr: ['quality = 0.65 × face score + 0.35 × top expression score'],
          points: [
            'First try: input size 160 with a score threshold of 0.15.',
            'If that misses: input size 224 with a threshold of 0.18.',
            'Each candidate face is scored with the quality formula.',
          ],
          example: 'A face score of 0.80 with a top expression score of 0.60 gives 0.65 × 0.80 + 0.35 × 0.60 = 0.73.',
        },
        {
          title: 'Low-light handling',
          what: 'Checks whether a frame is too dark to read, and retries on brightened copies when it is.',
          expr: ['averageLuma = mean(0.2126 R + 0.7152 G + 0.0722 B)'],
          terms: [
            ['R, G, B', 'the red, green and blue values of each pixel.'],
            ['Contrast', 'the standard deviation of luma.'],
          ],
          points: [
            'A frame counts as low light when averageLuma is below 82, or when averageLuma is below 105 and contrast is below 42.',
            'A low-light frame that finds no face is retried on two brightened copies: gamma 0.58, brightness +34, contrast 1.28, then gamma 0.72, brightness +24, contrast 1.16.',
          ],
          example: 'A frame with averageLuma 95 and contrast 30 counts as low light, because 95 is below 105 and 30 is below 42.',
        },
        {
          title: 'Calibration',
          what: 'Corrects the raw scores for the seven expressions (angry, disgusted, fearful, happy, neutral, sad, surprised) using a confusion-style table built from a labelled image set (expression-calibration.json).',
          expr: [
            'calibrated[true] = Σ over predicted of raw[predicted] × P(true | predicted)',
            'final = (1 − w) × raw + w × calibrated',
          ],
          terms: [
            ['raw', 'the model’s original score for an expression.'],
            ['P(true | predicted)', 'the table’s share of images with that true expression among those the model gave that prediction.'],
            ['w', 'the blend weight. The default is 0.35.'],
          ],
          points: [
            'The final scores are normalised to sum to 1.',
            'The table is ignored if it was built from fewer than 50 detected samples.',
          ],
          example: 'With w = 0.35, a raw score of 0.60 and a calibrated score of 0.40 blend to 0.65 × 0.60 + 0.35 × 0.40 = 0.53 before normalising.',
        },
        {
          title: 'Attention',
          what: 'Decides whether the learner is looking at the screen. The face is marked “Looking Away” if any one of these holds. Otherwise the state is “Focused”.',
          points: [
            'The face width is under 10% of the frame.',
            'The face centre is more than 0.45 from the frame centre on either axis.',
            'The head is turned and the eyes are off-centre, with nose offset and eye offset both above 0.5 of the face width.',
          ],
        },
        {
          title: 'Intervention trigger',
          what: 'Decides when to step in. A negative emotion that is smoothed, above the confidence threshold and sustained for 5 s triggers an LLM-written tip or motivational message. Neutral and happy trigger nothing.',
          table: {
            head: ['Setting', 'Default'],
            rows: [
              ['Capture / analysis interval', '250 ms'],
              ['Confidence threshold', '0.55'],
              ['Smoothing window', '3 samples'],
              ['Consecutive detections required', '2'],
              ['Negative emotion must persist', '5 s'],
              ['Tip cooldown', '15 s'],
              ['Tip display time', '2 s'],
              ['Looking away before a nudge', '8 s'],
            ],
          },
          note: 'These values are admin-tunable defaults. A tip view also adds a small bonus to that question’s DV (see Exercises & DV).',
        },
      ],
    },
    {
      id: 'm-gaps',
      nav: 'Knowledge gaps',
      title: 'Knowledge Gap Localization',
      kicker: 'Novelty 3',
      intro: 'Runs after a wrong answer and decides what kind of mistake it was, why it probably happened and what to revise.',
      items: [
        {
          title: 'Error type: rule layer',
          what: 'Names the kind of error by comparing the learner’s answer with the correct answer.',
          table: {
            head: ['Condition', 'Error type'],
            rows: [
              ['Numeric, difference ≤ 0.01', 'minor_calculation_error'],
              ['Numeric, difference ≤ 1', 'calculation_error'],
              ['Numeric, larger difference', 'major_calculation_error'],
              ['Text, ≥ 75% word overlap with the correct answer', 'minor_concept_error'],
              ['Text, some overlap', 'conceptual_misunderstanding'],
              ['Text, no overlap', 'complete_misunderstanding'],
            ],
          },
          example: 'An answer of 12 where 12.5 is correct differs by 0.5, so it is a calculation_error.',
        },
        {
          title: 'Error type: ML layer',
          what: 'Predicts the same error-type label from the wording of the question and the answers, and reports how sure it is.',
          terms: [
            ['Input', 'one text made of the concept, question, code, selected answer and correct answer.'],
            ['Pipeline', 'TF-IDF over unigrams and bigrams, followed by a class-balanced classifier.'],
            ['Output', 'the error-type label and the model’s confidence, which is the highest class probability.'],
            ['Fallback', 'if the model is missing, the rule layer is used.'],
          ],
        },
        {
          title: 'Probable cause (behaviour classifier)',
          what: 'Names the likely behavioural cause of a mistake. It runs only after a wrong answer. The rules are checked in order and the first match wins.',
          table: {
            head: ['#', 'Rule', 'Cause'],
            rows: [
              ['1', 'Answered in under 5 s on the first attempt, and topic accuracy ≥ 0.6', 'careless_mistake'],
              ['2', 'Same, but topic accuracy < 0.6', 'rushed_inattentive'],
              ['3', 'Negative emotion, and (attempts ≥ 3 or wrong count ≥ 3), and time over 20 s or unknown', 'frustrated_stuck'],
              ['4', 'Negative emotion, attempts ≤ 2, and time over 20 s', 'low_confidence_hesitation'],
              ['5', 'Specific error pattern, or (0.2 ≤ accuracy < 0.6 and attempts ≥ 2)', 'conceptual_misunderstanding'],
              ['6', 'Otherwise', 'knowledge_gap'],
            ],
          },
          points: [
            'Each cause also picks the revision question type: reinforcement, concept review, application or misconception correction.',
            'If no behavioural signals exist (camera denied, older client), the classifier falls back to accuracy and error pattern only.',
          ],
          example: 'A first-attempt wrong answer given in 3 s on a topic with 0.8 accuracy matches rule 1, so the cause is careless_mistake.',
        },
        {
          title: 'Intervention recommendation (Random Forest)',
          what: 'Recommends what the learner should do about the mistake, choosing one of 9 interventions.',
          terms: [
            ['Model', 'a scikit-learn pipeline ending in a Random Forest with 200 trees, class-balanced.'],
            ['One-hot encoded inputs', 'topic, difficulty level and emotion.'],
            ['16 numeric inputs (standard-scaled)', 'correctness, response time, attempts, hint use, confidence, hesitation, attention, previous performance, weak-topic count, session accuracy, revision history, learning progress, misconception score, careless-mistake probability, knowledge-gap score and cognitive load.'],
            ['The 9 interventions', 'Concept Review, Easy Revision Questions, Medium Revision Questions, Hard Revision Questions, Encourage Practice, Misconception Correction, Move to Next Topic, Provide Example and Step-by-Step Explanation.'],
          ],
          note: 'Two limits apply. Features the app cannot measure yet are set to neutral defaults (for example, hint use 0 and attention 0.5), and the training data is synthetic (360 rows).',
        },
        {
          title: 'Weak-topic ranking',
          what: 'Ranks the learner’s topics by weakness, so revision goes to the weakest first. Each input is averaged per topic over the learner’s gap reports.',
          expr: ['Weakness = 0.30 × KnowledgeGap + 0.25 × Misconception + 0.20 × (1 − SessionAccuracy) + 0.15 × Breadth + 0.10 × CarelessProbability'],
          terms: [
            ['Breadth', 'min(1, weak topics ÷ distinct topics).'],
            ['Range', 'the result is clamped to 0–1.'],
            ['Bands', 'above 0.7 = Hard, 0.4 to 0.7 = Medium, below 0.4 = Easy.'],
            ['Output', 'the top 2 topics are sent to the exercise generator.'],
          ],
          example: 'KnowledgeGap 0.8, Misconception 0.6, SessionAccuracy 0.5, Breadth 0.5 and CarelessProbability 0.2 give 0.24 + 0.15 + 0.10 + 0.075 + 0.02 = 0.585, which is Medium.',
        },
      ],
    },
    {
      id: 'm-difficulty',
      nav: 'Difficulty',
      title: 'Difficulty Prediction Model',
      kicker: 'Novelty 2',
      intro: 'Turns each answer into a score, an effort value and a readiness value, then predicts whether the next question should be Easy, Medium or Hard for this learner.',
      items: [
        {
          title: 'Composite score (0–10)',
          what: 'Scores one answer out of 10 from four parts.',
          expr: ['Total = Answer + Time + QDI + Attempts', 'NS = Total ÷ 10'],
          terms: [
            ['Answer', '6 if most correct, 3 if suitable, 0 if incorrect.'],
            ['Time', '+1 if the answer took 1.5 minutes or less.'],
            ['QDI', '+1 if the question’s QDI ≥ 0.4.'],
            ['Attempts', '2 for one attempt, 1 for two or three, 0 otherwise.'],
            ['NS', 'the normalised score, from 0 to 1.'],
          ],
          example: 'A suitable answer given in 2 minutes, on a question with QDI 0.5, at the second attempt scores 3 + 0 + 1 + 1 = 5, so NS = 0.5.',
          note: 'Correctness carries 60% of the score, so speed and attempts alone can never make a wrong answer look good.',
        },
        {
          title: 'Question Difficulty Index (QDI) and question criteria',
          what: 'Places each question in a band and sets what is expected of a learner at that band. QDI ≥ 0.7 is hard, ≥ 0.4 is medium, otherwise easy. Each band has explicit educational criteria, and intensity adjusts them within the band.',
          expr: ['intensity = 0.8 + 0.4 × QDI', 'Historical success = base − (QDI − 0.5) × 0.2'],
          table: {
            head: ['Band', 'Expected time', 'Concepts', 'Bloom’s level', 'Historical success'],
            rows: [
              ['Easy', '1.0 min', '1', '1 Remember', '0.85'],
              ['Medium', '2.0 min', '3', '2 Understand', '0.60'],
              ['Hard', '3.5 min', '5', '2 Understand', '0.35'],
            ],
          },
          points: [
            'Expected time and concept count are multiplied by intensity.',
            'Historical success starts from the band’s base value in the table and is kept between 0.05 and 0.95.',
            'Only Bloom levels 1 and 2 are used, because the system can only reliably verify Remember and Understand.',
          ],
          example: 'A medium question with QDI 0.6 has intensity 0.8 + 0.4 × 0.6 = 1.04, so its expected time is 2.0 × 1.04 = 2.08 min and its historical success is 0.60 − 0.02 = 0.58.',
        },
        {
          title: 'Cognitive effort',
          what: 'Measures how much effort an answer took, from 0 to 1. A higher value means the learner needed more effort.',
          expr: [
            'TimeRatio = actual time ÷ expected time',
            'TE = clamp((TimeRatio − 1) ÷ 2, 0, 1)',
            'AE = clamp((attempts − 1) ÷ 4, 0, 1)',
            'QE = 1 − answer value',
            'CE = 0.4 TE + 0.3 AE + 0.3 QE',
          ],
          terms: [
            ['TE', 'time effort.'],
            ['AE', 'attempt effort.'],
            ['QE', 'answer-quality effort.'],
            ['clamp(x, 0, 1)', 'keeps x between 0 and 1.'],
            ['Bands', 'below 0.4 is low, 0.7 or above is high, and anything between is moderate.'],
          ],
          example: 'Taking 4 minutes on a 2-minute question (TE 0.5), on the second attempt (AE 0.25), with a suitable answer (QE 0.5) gives CE = 0.20 + 0.075 + 0.15 = 0.425, which is moderate.',
        },
        {
          title: 'Recent history',
          what: 'Summarises how the learner has been doing lately. It uses the learner’s last 5 predictions, and only once they have at least 3. Before that, recent accuracy and learning trend are reported as “insufficient history”, not as a made-up 0.',
          expr: [
            'RA = share of those answers that were most-correct',
            'LT = clamp((mean score of the newer half − mean score of the older half) × 2, −1, 1)',
          ],
          terms: [
            ['RA', 'recent accuracy.'],
            ['LT', 'learning trend, from −1 to 1. A positive value means the newer answers scored higher.'],
          ],
          example: 'If the newer half averages 0.8 and the older half 0.6, LT = (0.8 − 0.6) × 2 = 0.4.',
          note: 'For the model’s internal maths only, the neutral defaults are RA = 0.5 and LT = 0.',
        },
        {
          title: 'Learner readiness',
          what: 'Estimates how ready the learner is for a harder question, from 0 to 1.',
          expr: [readinessFormula],
          terms: [
            ['NS', 'the normalised composite score.'],
            ['CE', 'cognitive effort.'],
            ['RA', 'recent accuracy.'],
            ['LT', 'learning trend. (LT + 1) ÷ 2 rescales it from −1…1 to 0…1.'],
            ['Bands', '≥ 0.7 high, < 0.4 low, otherwise moderate.'],
          ],
          example: 'NS 0.8, CE 0.3, RA 0.6 and LT 0.2 give 0.28 + 0.245 + 0.12 + 0.06 = 0.705, which is high.',
        },
        {
          title: 'Training labels',
          what: 'This is the decision rule the model learns: whether the next question steps up a band, holds or steps down. The rules are checked in order.',
          steps: [
            'LR ≥ 0.7 and CE ≤ 0.3: step up one band.',
            'NS ≥ 0.6 and CE ≥ 0.6: hold the current band.',
            'LR < 0.4, or (NS < 0.4 and CE ≥ 0.5): step down one band.',
            'LR ≥ 0.55: step up.',
            'LR ≤ 0.45: step down.',
            'Otherwise: hold.',
          ],
          example: 'LR 0.72 with CE 0.25 matches the first rule, so the learner steps up one band.',
        },
        {
          title: 'The classifier',
          what: 'Predicts Easy, Medium or Hard for the next question. The model is a Random Forest with 150 trees.',
          terms: [
            ['17 features', 'answer, time (min), QDI, attempts, composite score, NS, time ratio, CE, LR, recent accuracy, recent average effort, recent average attempts, learning trend, expected time, concept count, Bloom’s level and historical success rate.'],
            ['Training', '6,000 synthetic learners with 5% label noise and a fixed seed. The noise keeps the confidence realistic.'],
            ['Confidence shown', 'the highest class probability (the share of trees that agree).'],
          ],
          example: 'If 120 of the 150 trees agree on Medium, the confidence shown is 0.80.',
        },
        {
          title: 'Safety layers',
          what: 'Keeps the prediction sensible when the answer was wrong or the model is unavailable.',
          terms: [
            ['Guardrail', 'a fully incorrect answer can never move the learner to a harder band than the one just answered. It can still hold or step down. These cases are tagged _guardrail_capped.'],
            ['Fallback', 'if the model is unavailable, NS ≥ 0.7 gives hard, NS ≥ 0.4 gives medium, and anything lower gives easy. These cases are tagged as fallback.'],
            ['Basic level', 'Basic-level questions skip the model and keep the learner at Medium.'],
            ['Explanation', 'every prediction stores a plain-language explanation: readiness, effort, answer type, attempts and trend.'],
          ],
        },
      ],
    },
    {
      id: 'm-exercises',
      nav: 'Exercises & DV',
      title: 'Adaptive Coding Exercise Generator and the DV value',
      kicker: 'Novelty 4',
      intro: 'Writes the questions, serves them by difficulty bucket and recalculates each question’s difficulty from real use.',
      items: [
        {
          title: 'What DV is',
          what: 'DV (difficulty value) is how hard learners actually found a question. It is not a guess by the LLM.',
          expr: ['DV = times marked hard ÷ times displayed'],
          points: [
            'DV is a proportion from 0 to 1.',
            'It is only recalculated once a question has been displayed at least minSample times. The default is 5.',
          ],
          example: 'A question displayed 20 times and marked hard 6 times has DV = 6 ÷ 20 = 0.3.',
        },
        {
          title: 'Re-bucketing',
          what: 'Moves each question to the bucket its DV points to. It runs as a nightly job, or from “recalculate now” in admin.',
          table: {
            head: ['DV', 'Bucket'],
            rows: [
              ['≤ 0.3', 'Easy'],
              ['≤ 0.7', 'Medium'],
              ['> 0.7', 'Hard'],
            ],
          },
          points: [
            'Combined (multi-topic) questions stay in the combined bucket, but their DV is still tracked.',
            'A question with DV ≥ 0.9 (the replace threshold) is retired and a new one is generated in the same bucket and topic. Each bucket is then topped up to 10 questions.',
            'Every DV change is written to a DV update log with before/after values, the source and the event type.',
          ],
          example: 'A question whose DV rises from 0.65 to 0.75 moves from Medium to Hard.',
        },
        {
          title: 'Tip-view bonus',
          what: 'Links emotion to content. When a learner opens the tip banner for a question, that question’s DV rises by 0.01, capped at maxDvScore = 1. This is how affect data nudges content calibration.',
          expr: ['DV = min(DV + 0.01, maxDvScore)'],
          example: 'A question at DV 0.42 moves to 0.43 after one tip view.',
        },
        {
          title: 'Question generation',
          what: 'Writes the Java multiple-choice questions. The model is gpt-4o-mini by default.',
          points: [
            'Each question has a code snippet and four options: one most-correct, one valid but less complete, and two distractors.',
            'Each question has a Bloom level of 1 or 2.',
            'Topics rotate through the syllabus, or follow the learner’s weak topics.',
          ],
        },
        {
          title: 'Session structure',
          what: 'Sets the shape of a learner’s session. All values are defaults and admin-tunable.',
          terms: [
            ['Diagnostic', '3 questions.'],
            ['Course', '5 questions at 12 minutes each.'],
            ['Revision', '3 questions per weak topic.'],
          ],
        },
        {
          title: 'Revision difficulty',
          what: 'Chooses the difficulty of the next revision round from how the last one went.',
          expr: ['performance = 0.7 × accuracy + 0.3 × average answer score'],
          points: [
            'The score is sent to the Difficulty Prediction Model as a request tagged revision_difficulty.',
            'If that service fails, the local fallback is: below 0.45 gives easy, below 0.75 gives medium, and anything higher gives hard.',
          ],
          example: 'Accuracy 0.6 with an average answer score of 0.7 gives 0.42 + 0.21 = 0.63, which the fallback reads as medium.',
        },
        {
          title: 'Combined (“harder”) mode',
          what: 'Decides when a learner is ready for questions that mix topics.',
          points: [
            'It triggers when rolling accuracy on a topic is at least 0.8 over at least 5 answers, and at least 2 topics are mastered.',
            'A combined question then mixes 2 to 4 topics.',
          ],
          example: '4 correct out of the last 5 answers on a topic is 0.8, which meets the accuracy condition.',
        },
      ],
    },
    {
      id: 'm-analytics',
      nav: 'Analytics',
      title: 'Unified Learning Analytics',
      kicker: 'Novelty 5',
      intro: 'An aggregation job runs every 5 minutes. It reads all component databases (read-only) and rebuilds each learner’s profile.',
      items: [
        {
          title: 'Profile measures',
          what: 'These are the building blocks of the profile. All are on a 0–100 scale.',
          terms: [
            ['Accuracy', 'correct ÷ attempted, across placement quizzes, course answers and revision answers.'],
            ['KnowledgeGap', 'knowledge_gap events ÷ wrong answers.'],
            ['Misconception', 'conceptual_misunderstanding events ÷ wrong answers.'],
            ['Careless', 'careless_mistake events ÷ wrong answers.'],
            ['Confidence', '100 − (low_confidence_hesitation + frustrated_stuck) ÷ wrong answers × 100.'],
            ['EmotionalStability', 'non-negative emotion samples ÷ all emotion samples.'],
            ['Competency', 'mean composite score (0–10) × 10.'],
          ],
          example: 'A learner with 20 wrong answers, of which 3 were low_confidence_hesitation and 2 were frustrated_stuck, has Confidence = 100 − 5 ÷ 20 × 100 = 75.',
        },
        {
          title: 'Overall learning score',
          what: 'One number for how the learner is doing. It rises with accuracy and confidence and as the knowledge-gap score falls.',
          expr: ['Score = 0.4 × Accuracy + 0.3 × (100 − KnowledgeGap) + 0.3 × Confidence'],
          example: 'Accuracy 70, KnowledgeGap 40 and Confidence 75 give 28 + 18 + 22.5 = 68.5.',
          note: 'A missing part counts as 50.',
        },
        {
          title: 'Schema development level',
          what: 'Places the learner on a four-step ladder, using their competency.',
          table: {
            head: ['Competency', 'Level'],
            rows: [
              ['Below 25', 'novice'],
              ['Below 50', 'developing'],
              ['Below 75', 'competent'],
              ['75 or above', 'proficient'],
            ],
          },
          points: [
            'Cognitive alignment is checked only for two pairs: novice with Bloom’s Remember, and developing with Bloom’s Understand.',
            'With at least 5 answers at that level, 0.7 or higher accuracy means “progressing” and lower means “recall locked”.',
          ],
          example: 'A mean composite score of 6.2 gives Competency 62, which is competent.',
        },
        {
          title: 'Pathway stage',
          what: 'Places the learner in one of four stages: foundation, building, consolidating or advanced.',
          steps: [
            'Fewer than 10 attempts: foundation.',
            'Otherwise start from the schema level: novice and developing give building, competent gives consolidating, proficient gives advanced.',
            'Refine with the overall score: a building learner with a score of 85 or more moves up to consolidating. A score below 35 drops advanced to consolidating, or consolidating to building.',
            'Emotional cap: if emotional stability is below 40, advanced drops to consolidating and consolidating drops to building. The profile is flagged “emotion-capped”.',
            'If there is no schema data, use the score alone: below 50 is building, below 75 is consolidating, otherwise advanced.',
          ],
          example: 'A proficient learner with emotional stability 35 is held at consolidating and flagged “emotion-capped”.',
        },
        {
          title: 'Next action',
          what: 'Picks one recommended next action from a priority-ordered rule table. The first match wins.',
          table: {
            head: ['Priority', 'Condition', 'Action'],
            rows: nextActions,
          },
          example: 'A learner whose dominant mistake is knowledge gap, with KnowledgeGap 72, gets “Review prerequisites”.',
        },
        {
          title: 'Supporting indicators',
          what: 'Extra indicators shown on the profile next to the main score.',
          terms: [
            ['Topic mastery', 'accuracy of 0.7 or more is strong, 0.4 or more is developing, below 0.4 is weak.'],
            ['Revision effectiveness', 'mean of (revision accuracy − original accuracy) per topic.'],
            ['Improvement trend', 'a least-squares slope over the last sessions. A slope above +5 is improving, below −5 is declining, and anything else is stable. The projected next score is last score + slope. This is trend extrapolation, not a trained model.'],
            ['Learning consistency', '100 × (1 − standard deviation ÷ mean) of session scores.'],
            ['Cognitive load estimate', '(mean of time ÷ expected time) ÷ 2 × 100, with expected times of 10, 20 and 35 s for easy, medium and hard.'],
            ['Learning style label', 'fast or slow (median time of 15 s or less is fast) combined with accurate or inaccurate (accuracy of 60 or more is accurate). This is a behavioural label, not a validated model.'],
            ['Activity streak', 'consecutive UTC days with a session, ending today or yesterday.'],
          ],
          example: 'Session scores with a mean of 70 and a standard deviation of 7 give a learning consistency of 100 × (1 − 0.1) = 90.',
        },
      ],
    },
  ],
  limits: {
    title: 'Honesty and limits',
    points: [
      'The difficulty, knowledge-gap and intervention models are trained on synthetic, rule-labelled data. Their accuracy shows the pipelines work, not that predictions are right for real learners.',
      'Some inputs have no real measurement yet and use fixed neutral defaults: hint use, attention, learning progress and self-reported confidence.',
      'Every model has a tagged fallback, and every decision is visible on the admin dashboard.',
      'Thresholds are admin-tunable, so the values shown are defaults.',
    ],
  },
};

export const services = [
  ['Frontend', 'React, served by nginx', 'Single-page app that talks only to the gateway.'],
  ['API gateway', 'Node.js, Express', 'Single entry point. Routes requests, applies the CORS policy, logs requests and returns structured errors when a backend is down.'],
  ['User login', 'Node.js, Express', 'Google OAuth 2.0 sign-in, JSON Web Tokens, revocable sessions and role-based access.'],
  ['Emotion service', 'Node.js, Express, face-api.js', 'Emotion recognition, smoothing and intervention triggers.'],
  ['Difficulty prediction', 'Python, Flask, scikit-learn', 'Hosts the Random Forest classifier and tracks each learner’s session state.'],
  ['Knowledge-gap localization', 'Python, Flask', 'Error-type, concept and behavioural-cause classification.'],
  ['Coding-exercise generator', 'Node.js, Express, OpenAI API', 'Question generation, delivery and nightly recalibration.'],
  ['Learning analytics', 'Node.js, Express, node-cron', 'Scheduled aggregation into learner profiles.'],
  ['Database', 'MongoDB 7', 'One instance holding a separate logical database for each component.'],
];

export const architectureNote =
  'Every service runs in its own container, orchestrated by one Docker Compose definition. Each research component owns its own database, and the analytics service reads across them without write access.';

export const evaluation = {
  rows: [
    ['Emotion recognition', 'Held-out test set', 'CNN from scratch, 35.10%', 'MobileNetV2 transfer learning, 54.07%'],
    ['Difficulty prediction', '2,000 synthetic records', 'Gradient Boosting, 94.0%', 'Random Forest, 94.6%'],
    ['Knowledge-gap classification', '300 synthetic mistake records', 'Logistic Regression, 90.3%', 'Random Forest, 97.2%'],
  ],
  caveat:
    'These figures show that the pipelines work, not that the system is accurate with real learners. The difficulty and knowledge-gap models were trained and tested on synthetic, rule-labelled data, and the emotion model’s moderate accuracy needs further training under real classroom conditions. The next stage of evaluation uses authentic learner data and expert review of the generated questions.',
};

// A milestone shows its `when` line only if one is set, so assessment dates can be added later.
export const milestones = [
  {
    name: 'Topic assessment',
    when: '13 January 2026',
    detail: 'The topic, research gap and member sub-objectives were reviewed and approved by the supervisor and co-supervisor.',
  },
  {
    name: 'Planning and requirements',
    when: 'Completed',
    detail: 'Each member reviewed the literature for their own component and defined its functional requirements. The team set the shared non-functional requirements, including performance, security, scalability and login, and confirmed that every member owns one full component plus an equal share of the shared work.',
  },
  {
    name: 'System and design',
    when: 'Completed',
    detail: 'The microservice architecture, per-service sequence diagrams, database schemas and login flow were designed, followed by wireframes for every screen and the project proposal document.',
  },
  {
    name: 'Development',
    when: 'Started April 2026, completed',
    detail: 'Each member built their component’s service, model and screens and registered its routes in the API gateway. The team built Unified Learning Analytics together and reviewed each other’s pull requests in rotating pairs, so nobody approved their own code.',
  },
  {
    name: 'Testing and deployment',
    when: 'Completed',
    detail: 'Unit tests for every service, integration tests through the gateway, and user acceptance sessions with feedback collected for each component. Request and error logging was added across services and the full stack was deployed with Docker.',
  },
  {
    name: 'Research paper and final submission',
    when: 'Completed',
    detail: 'Final report chapters, API documentation and user manual, plus the research paper “Designing and Developing Programming Education Learning Pathways Based on Schema and Emotion Analysis”.',
  },
  {
    name: 'Live for learners',
    when: '',
    detail: 'SkillBridge is running at slskillbridgex.com.',
  },
];

export const team = [
  { name: 'Wathulanda S.R.W.H.N.B', id: 'IT22253026', role: 'Adaptive Coding Exercise Generator', email: 'it22253026@my.sliit.lk', photo: wathulanda, also: 'Also built user login and led the deployment.' },
  { name: 'Wakishta C.S', id: 'IT22125620', role: 'Difficulty Prediction Model', email: 'it22125620@my.sliit.lk', photo: wakishta },
  { name: 'Pramodhya M.V.P.', id: 'IT22540508', role: 'Knowledge Gap Localization', email: 'it22540508@my.sliit.lk', photo: pramodhya },
  { name: 'Samarasinghe B.M.S.V', id: 'IT22543400', role: 'Emotion-Triggered Interventions', email: 'it22543400@my.sliit.lk', photo: samarasinghe },
];

export const supervisors = [
  { name: 'Dr. Junius Anjana Vidanaralage', role: 'Supervisor', email: 'junius.a@sliit.lk' },
  { name: 'Ms. Thisara Shyamalee', role: 'Co-supervisor', email: 'thisara.s@sliit.lk' },
];

export const contact = {
  title: 'Contact us',
  intro: 'Have a question about SkillBridge or the research behind it? Send us a message and we will reply by email.',
  email: 'nadeejawathulanda@gmail.com',
};

export const affiliation =
  'Department of Information Technology, Sri Lanka Institute of Information Technology, Malabe, Sri Lanka';
